/*
 * Wishlist Render — draws the drawer card list from BroraWishlist data.
 * On `brora:wishlist:changed`, reconciles cards (keyed by item id) into
 * [data-wishlist-drawer-cards] by cloning <template data-wishlist-card-template>
 * (snippets/wishlist-card.liquid — single source, Tailwind-scanned) and filling
 * [data-slot] hooks via textContent/setAttribute (no innerHTML; hrefs via
 * safeUrl). Keyed updates preserve in-progress states (undo) + loaded images.
 * View only — never touches window.WishlistKing.
 */
;(function () {
  'use strict';

  function slot(root, name) {
    return root.querySelector('[data-slot="' + name + '"]');
  }

  // The shop's money format, served by layout/theme.liquid. Shop-level, so it carries the shop's
  // currency rather than the market's — the market-aware value is window.broraVoyado.moneyFormat,
  // which money() reaches for first.
  function shopMoneyFormat() {
    return window.broraShop.moneyFormat;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const fixed = ((Number(cents) || 0) / 100).toFixed(precision);
    const parts = fixed.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return intPart + (precision && parts[1] ? decimal + parts[1] : '');
  }

  // Prefers the shared market-aware formatter (voyado-price-storefront.js exposes it) so a second parser
  // can't drift from it, and parses the market's own format only when that formatter is absent. Every
  // Shopify money placeholder is matched, not just {{amount}} — a market on PLN or EUR carries
  // {{amount_with_comma_separator}}.
  function money(cents) {
    const bv = window.broraVoyado;
    if (bv && typeof bv.formatMoney === 'function') {
      const out = bv.formatMoney((Number(cents) || 0) / 100, bv.currencyCode);
      if (out) return out;
    }
    const format = shopMoneyFormat();
    const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return format;
    let value;
    switch (m[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', '.'); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2, ',', '.'); break; // {{amount}}
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  // Same as money(), but returns HTML carrying the AED dirham glyph (voyado-price-storefront.js exposes
  // it). Used only for the price/compare slots — static markup + a numeric amount, so innerHTML is safe;
  // the title and other user-data slots stay on textContent.
  function moneyHtml(cents) {
    const bv = window.broraVoyado;
    if (bv && typeof bv.formatMoneyHtml === 'function') {
      const out = bv.formatMoneyHtml((Number(cents) || 0) / 100, bv.currencyCode);
      if (out) return out;
    }
    return money(cents);
  }

  // Block javascript:/data: in the card link; allow relative Shopify paths
  // and absolute http(s) only (defence-in-depth against a hostile API URL).
  function safeUrl(url) {
    const s = String(url == null ? '' : url).trim();
    if (!s) return '#';
    if (s.charAt(0) === '/') return s;
    try {
      const u = new URL(s, window.location.origin);
      if (u.protocol === 'https:' || u.protocol === 'http:') return u.href;
    } catch (e) {}
    return '#';
  }

  function withWidth(url, w) {
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  function fillImage(img, url, alt) {
    if (!img) return;
    if (!url) { img.remove(); return; }
    img.setAttribute('src', withWidth(url, 320));
    img.setAttribute('srcset', withWidth(url, 144) + ' 144w, ' + withWidth(url, 160) + ' 160w, ' + withWidth(url, 320) + ' 320w');
    img.setAttribute('alt', alt || '');
  }

  function buildCard(tmpl, item) {
    const node = tmpl.content.firstElementChild.cloneNode(true);
    const product = item.product || {};
    const variants = product.variants || [];
    const variant = variants.filter(function (v) { return String(v.id) === String(item.variant_id); })[0] || variants[0] || {};

    node.setAttribute('data-wishlist-drawer-item-id', item.id != null ? item.id : '');
    node.setAttribute('data-wishlist-drawer-product-id', item.product_id != null ? item.product_id : '');
    node.setAttribute('data-wishlist-drawer-variant-id', item.variant_id != null ? item.variant_id : '');
    node.setAttribute('data-wishlist-drawer-product-handle', product.handle || '');

    const url = safeUrl(product.url || (product.handle ? '/products/' + product.handle : ''));
    const title = product.title || '';

    const imageLink = slot(node, 'image-link');
    if (imageLink) { imageLink.setAttribute('href', url); imageLink.setAttribute('aria-label', title); }
    const titleLink = slot(node, 'title-link');
    if (titleLink) { titleLink.setAttribute('href', url); titleLink.textContent = title; }

    const primaryImg = slot(node, 'image-primary');
    fillImage(primaryImg, product.featured_image || (product.images && product.images[0]) || '', title);
    const secondUrl = (product.images && product.images[1]) || '';
    fillImage(slot(node, 'image-secondary'), secondUrl, '');
    // No second image → no hover-swap: the primary must not fade out on hover (it would reveal a blank slot).
    if (!secondUrl && primaryImg) primaryImg.classList.remove('group-hover/wlimage:opacity-0');

    const priceCents = variant.price != null ? variant.price : product.price_min;
    const priceEl = slot(node, 'price');
    if (priceEl) {
      priceEl.innerHTML = moneyHtml(priceCents);
      priceEl.setAttribute('data-wishlist-product-id', item.product_id != null ? item.product_id : '');
    }

    const compareCents = variant.compare_at_price;
    const compareEl = slot(node, 'compare');
    if (compareEl) {
      if (compareCents != null && Number(compareCents) > Number(priceCents)) compareEl.innerHTML = moneyHtml(compareCents);
      else compareEl.remove();
    }

    const colourEl = slot(node, 'colour');
    if (colourEl) {
      let opt = variant.option1 || '';
      if (opt === 'Default Title') opt = '';
      colourEl.textContent = opt;
      colourEl.classList.toggle('hidden', !opt);
    }

    return node;
  }

  // Keyed reconcile: reuse existing card nodes, add new, drop gone (in list
  // order). Cards mid-undo (data-state=removed) are left for the drawer JS to
  // animate out, so we don't fight its removal.
  function reconcile(region, list) {
    const tmpl = document.querySelector('[data-wishlist-card-template]');
    if (!tmpl) { console.warn('[wishlist] template [data-wishlist-card-template] not found'); return; }
    const existing = {};
    region.querySelectorAll('[data-wishlist-drawer-card]').forEach(function (n) {
      existing[n.getAttribute('data-wishlist-drawer-item-id')] = n;
    });
    const wanted = {};
    let prev = null;
    list.forEach(function (item) {
      const id = String(item.id);
      wanted[id] = true;
      const node = existing[id] || buildCard(tmpl, item);
      const ref = prev ? prev.nextSibling : region.firstChild;
      if (node !== ref) region.insertBefore(node, ref);
      prev = node;
    });
    Object.keys(existing).forEach(function (id) {
      if (wanted[id]) return;
      const node = existing[id];
      if (node.getAttribute('data-wishlist-drawer-state') === 'removed') return;
      node.remove();
    });
  }

  function toggleRegions(drawer, mode) {
    drawer.setAttribute('data-wishlist-drawer-state', mode);
    const region = drawer.querySelector('[data-wishlist-drawer-cards]');
    const emptyRegion = drawer.querySelector('[data-wishlist-drawer-empty]');
    const footerEmpty = drawer.querySelector('[data-wishlist-drawer-footer-empty]');
    const footerItems = drawer.querySelector('[data-wishlist-drawer-footer-items]');
    const loggedOutBanner = drawer.querySelector('[data-wishlist-drawer-logged-out]');
    const isLoggedIn = drawer.getAttribute('data-wishlist-drawer-is-logged-in') === 'true';
    if (region) { region.classList.toggle('hidden', mode !== 'items'); region.classList.toggle('flex', mode === 'items'); }
    if (emptyRegion) { emptyRegion.classList.toggle('hidden', mode !== 'empty'); emptyRegion.classList.toggle('flex', mode === 'empty'); }
    if (footerEmpty) footerEmpty.classList.toggle('hidden', mode !== 'empty');
    if (footerItems) { footerItems.classList.toggle('hidden', mode !== 'items'); footerItems.classList.toggle('flex', mode === 'items'); }
    if (loggedOutBanner) {
      const showBanner = mode === 'items' && !isLoggedIn;
      loggedOutBanner.classList.toggle('hidden', !showBanner);
      loggedOutBanner.classList.toggle('flex', showBanner);
    }
  }

  // custom.colour label per product → [data-slot=pattern]. One batched Storefront API
  // query, cached per product id; the slot stays hidden when the product has no
  // pattern. Degrades silently.
  const colourCache = {};
  // Storefront API credentials, served by layout/theme.liquid.
  function storefrontConfig() {
    const c = window.broraStorefront;
    return (c && c.token && c.domain && c.version) ? c : null;
  }
  // custom.colour label → its own line ([data-slot=pattern]), below the option.
  function fillPatterns() {
    document.querySelectorAll('[data-wishlist-drawer-card]').forEach(function (card) {
      const patternEl = slot(card, 'pattern');
      const colourEl = slot(card, 'colour');
      const label = colourCache[card.getAttribute('data-wishlist-drawer-product-id')];
      if (label == null) return;
      if (patternEl) {
        patternEl.textContent = label;
        patternEl.classList.toggle('hidden', !label);
      }
      // Show custom.colour label instead of size when the metafield is present.
      if (colourEl) colourEl.classList.toggle('hidden', !!label);
    });
  }
  function loadColours(list) {
    const cfg = storefrontConfig();
    const miss = [];
    list.forEach(function (it) {
      const id = it.product_id != null ? String(it.product_id) : '';
      if (id && !(id in colourCache) && miss.indexOf(id) < 0) miss.push(id);
    });
    if (!cfg || !miss.length) return Promise.resolve();
    const q = 'query($ids:[ID!]!){nodes(ids:$ids){...on Product{id metafield(namespace:"custom",key:"colour"){reference{...on Metaobject{field(key:"label"){value}}}}}}}';
    return fetch('https://' + cfg.domain + '/api/' + cfg.version + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
      body: JSON.stringify({ query: q, variables: { ids: miss.map(function (id) { return 'gid://shopify/Product/' + id; }) } })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
      ((d && d.data && d.data.nodes) || []).forEach(function (n) {
        if (!n || !n.id) return;
        const ref = n.metafield && n.metafield.reference;
        colourCache[n.id.split('/').pop()] = (ref && ref.field && ref.field.value) || '';
      });
      miss.forEach(function (id) { if (!(id in colourCache)) colourCache[id] = ''; });
    }).catch(function () {});
  }

  function render(detail) {
    const drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    const region = drawer.querySelector('[data-wishlist-drawer-cards]');
    const list = (detail && detail.items) || [];
    if (region) reconcile(region, list);
    if (region && window.broraVoyado && window.broraVoyado.applyPrices) window.broraVoyado.applyPrices(region);
    toggleRegions(drawer, list.length > 0 ? 'items' : 'empty');
    fillPatterns();
    loadColours(list).then(fillPatterns);
  }

  document.addEventListener('brora:wishlist:changed', function (e) { render(e.detail); });
})();
